import { useCallback } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  useAddSpanToListMutation,
  useCreateListMutation,
  useGetListsQuery,
} from '@/features/customListSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';

import type { CustomListCreate } from '@/generated/types/navigator/types.gen';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { CustomListOptions } from './CustomListOptions';
import ListDetailsForm from './ListDetailsForm';
import { useRunListAction } from '@/views/list/custom-lists/customListSelectorUtils';

interface CustomListIndividualSelectorProps {
  onCloseHandler: () => void;
  level2: string;
  level3: string;
}

export function CustomListIndividualSelector({
  onCloseHandler,
  level2,
  level3,
}: CustomListIndividualSelectorProps) {
  const { data: lists } = useGetListsQuery({
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const intl = useClientTerminology();
  const [createList, { isLoading: isCreatingList }] = useCreateListMutation();
  const [addSpanToList] = useAddSpanToListMutation();

  const [
    newListModalOpened,
    { open: openNewListModal, close: closeNewListModal },
  ] = useDisclosure(false);

  const runListAction = useRunListAction(onCloseHandler);

  const handleItemClick = useCallback(
    (id: string) => {
      if (id === 'new') {
        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.projects.creation.newProject,
          },
        });
        openNewListModal();
      } else {
        const selectedList = lists?.find((l) => l.id === id);
        if (!selectedList) return;
        runListAction(async () => {
          TagManager.dataLayer({
            dataLayer: {
              event: GoogleEvents.projects.adding.individual,
              params: {
                projectName: selectedList.name,
                projectDescription: selectedList.description,
              },
            },
          });

          return addSpanToList({ listId: id, level2, level3 }).unwrap();
        });
      }
    },
    [openNewListModal, lists, runListAction, addSpanToList, level2, level3]
  );

  const handleNewListSubmit = async (values: {
    name: string;
    description?: string;
  }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.newProjectSaved,
        params: {
          projectName: values.name,
          projectDescription: values.description,
        },
      },
    });

    const createListProps: CustomListCreate = {
      name: values.name,
      description: values.description ?? '',
    };

    await runListAction(async () => {
      const newList = await createList(createListProps).unwrap();

      return addSpanToList({
        listId: newList.summary.id,
        level2,
        level3,
      }).unwrap();
    });
    closeNewListModal();
  };

  return (
    <>
      <CustomListOptions onItemClick={handleItemClick} />
      <ListDetailsForm
        opened={newListModalOpened}
        onClose={closeNewListModal}
        onSubmit={handleNewListSubmit}
        isSaving={isCreatingList}
        title={intl.formatMessage({
          id: 'customList.addToNewList',
        })}
        submitButtonText={intl.formatMessage({
          id: 'customList.addToList',
        })}
      />
    </>
  );
}
